import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { UserPlus, LogIn, ListOrdered, Scale, Mail, Clock, Sparkles, HeartHandshake, Flower2 } from "lucide-react";
import axios from "axios";
import DahliasShell from "../components/dahlias/DahliasShell";

const BERRY = "#6E1E3A";

const STEPS = [
  { Icon: UserPlus, title: "1 · Create your free account", body: "Every dream list needs an owner. Sign up for Heritage HQ (or sign in if you already have one), it takes a minute and it's free." },
  { Icon: ListOrdered, title: "2 · Build your dream list", body: "When the sale opens, browse the release and save up to 15 varieties, dragged into your order of preference. Number one is your most-wanted. Edit anytime until close." },
  { Icon: Clock, title: "3 · The sale closes", body: "No racing, no crashing carts. Everyone gets the same window. When it closes, lists are locked and the allocation begins." },
  { Icon: Scale, title: "4 · Fair allocation", body: "Round by round, everyone receives their highest available choice, first choices first, then second choices, and so on until stock runs out or you reach 10 tubers. No one can scoop the rare ones before everyone's had a turn." },
  { Icon: Mail, title: "5 · Your allocation & invoice", body: "You'll see your allocated tubers right in your HQ (plus an email), with an invoice and simple bank-transfer payment. Pay within 48 hours to lock them in." },
];

export default function FairitagePage() {
  const [gardeners, setGardeners] = useState(null);
  useEffect(() => {
    axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/fairsale/interest`)
      .then((r) => setGardeners(r.data.gardeners))
      .catch(() => {});
  }, []);

  return (
    <DahliasShell>
      {/* Hero */}
      <section className="bg-[var(--dahlias-forest)] text-[#FBE6D8]">
        <div className="max-w-5xl mx-auto px-5 md:px-8 py-20 md:py-28 text-center">
          <span className="font-label tracking-[0.5em] text-[11px] uppercase text-[#FBE6D8]/70">
            ✦ No more checkout wars ✦
          </span>
          <h1 className="mt-5 font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05]">
            the <span className="font-display-italic" style={{ color: "#E8B4C8" }}>Fairitage</span> Tuber Sale
          </h1>
          <p className="mt-3 font-label tracking-[0.35em] text-[11px] uppercase text-[#FBE6D8]/60">by Heritage Dahlias</p>
          <p className="mt-6 text-base md:text-lg text-[#FBE6D8]/85 max-w-2xl mx-auto leading-relaxed">
            Instead of fastest-fingers-first, everyone submits their dream list during the sale window ,
            then our allocation engine shares the tubers out as fairly as possible. Everyone gets a fair chance
            at the rare ones.
          </p>
          <div className="mt-9 flex flex-col sm:flex-row items-center justify-center gap-3">
            <Link to="/account/signup" data-testid="fairitage-signup" className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full text-sm font-semibold tracking-wide text-white transition-colors hover:opacity-90" style={{ backgroundColor: BERRY }}>
              <UserPlus className="w-4 h-4" /> Create your free account
            </Link>
            <Link to="/account/login" data-testid="fairitage-signin" className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full text-sm font-semibold tracking-wide border-2 border-[#FBE6D8]/40 hover:bg-[#FBE6D8]/10 transition-colors">
              <LogIn className="w-4 h-4" /> Sign in to Heritage HQ
            </Link>
          </div>
          <p className="mt-5 text-xs text-[#FBE6D8]/55 italic" data-testid="fairitage-coming-soon-note">
            The Fairitage Sale tab is now in your HQ, sale opening soon. Sign up now so you're ready.
          </p>
          {gardeners > 0 && (
            <div
              data-testid="fairitage-interest-counter"
              className="mt-7 inline-flex items-center gap-2.5 px-5 py-2.5 rounded-full border border-[#FBE6D8]/30 bg-[#FBE6D8]/10"
            >
              <Flower2 className="w-4 h-4" style={{ color: "#E8B4C8" }} />
              <span className="font-display text-lg leading-none" style={{ color: "#E8B4C8" }}>{gardeners}</span>
              <span className="font-label tracking-[0.25em] text-[10px] md:text-[11px] uppercase text-[#FBE6D8]/80">
                gardeners already signed up
              </span>
            </div>
          )}
        </div>
      </section>

      {/* How it works */}
      <section className="bg-[var(--dahlias-cream)] text-[var(--dahlias-ink)]">
        <div className="max-w-5xl mx-auto px-5 md:px-8 py-16 md:py-24">
          <div className="text-center mb-12">
            <span className="font-label tracking-[0.5em] text-[11px] uppercase" style={{ color: BERRY }}>✦ How it works ✦</span>
            <h2 className="mt-4 font-display text-3xl md:text-4xl text-[var(--dahlias-forest)]">
              Everyone gets a <span className="font-display-italic" style={{ color: BERRY }}>fair go.</span>
            </h2>
          </div>
          <div className="space-y-4 max-w-3xl mx-auto">
            {STEPS.map((s) => (
              <div key={s.title} className="flex items-start gap-4 bg-[var(--dahlias-paper)] border-2 border-[var(--dahlias-sage)]/20 p-5 md:p-6 wobble-a" data-testid={`fairitage-step-${s.title[0]}`}>
                <span className="flex-shrink-0 w-11 h-11 rounded-full flex items-center justify-center text-white" style={{ backgroundColor: BERRY }}>
                  <s.Icon className="w-5 h-5" />
                </span>
                <div>
                  <h3 className="font-display text-lg md:text-xl text-[var(--dahlias-forest)]">{s.title}</h3>
                  <p className="mt-1 text-sm md:text-base text-[var(--dahlias-ink)]/75 leading-relaxed">{s.body}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-12 grid sm:grid-cols-2 gap-4 max-w-3xl mx-auto">
            <div className="border-2 p-5 bg-white wobble-b" style={{ borderColor: `${BERRY}33` }}>
              <Sparkles className="w-5 h-5 mb-2" style={{ color: BERRY }} />
              <h3 className="font-display text-lg text-[var(--dahlias-forest)]">A &amp; B releases</h3>
              <p className="mt-1 text-sm text-[var(--dahlias-ink)]/75">Premium collectors' varieties (think Café au Lait, Hamari Rose) sit in the A release, with beautiful, more available varieties in B, each with clear, fixed pricing. No bidding, no surprises.</p>
            </div>
            <div className="border-2 p-5 bg-white wobble-b" style={{ borderColor: `${BERRY}33` }}>
              <HeartHandshake className="w-5 h-5 mb-2" style={{ color: BERRY }} />
              <h3 className="font-display text-lg text-[var(--dahlias-forest)]">Why we do it this way</h3>
              <p className="mt-1 text-sm text-[var(--dahlias-ink)]/75">Release nights used to mean crashed pages and empty carts. Fairitage removes the panic, your list does the queuing for you, and the garden gets shared around.</p>
            </div>
          </div>

          <div className="mt-14 text-center">
            <Link to="/account/signup" data-testid="fairitage-signup-bottom" className="inline-flex items-center gap-2 px-8 py-4 rounded-full text-sm font-semibold tracking-wide text-white transition-colors hover:opacity-90" style={{ backgroundColor: BERRY }}>
              <UserPlus className="w-4 h-4" /> Sign up &amp; be ready for the first Fairitage Sale
            </Link>
          </div>
        </div>
      </section>
    </DahliasShell>
  );
}
